/**
 * Strip noise (tracking, search, session) from a pasted supplier URL.
 *
 * Per-host strategies — start conservative and only add a rule when a
 * supplier's product URL carries query/fragment cruft that's not
 * required to re-fetch the product:
 *
 *   - amazon.com.au (and other Amazon TLDs) — the path (`/dp/<asin>`)
 *     already identifies the product. Drop the entire query + fragment.
 *   - broadwaycomputers.net.au — uses `?product=<sku>` as the canonical
 *     identifier and tacks `&search_content=…` (current search) on
 *     after. Keep only the first query parameter; drop the rest.
 *
 * Default — return the URL unchanged. Unknown hosts may carry essential
 * query params (e.g. an ID in the second slot, multi-key SKUs), so we
 * don't gamble on cleanup we haven't validated.
 *
 * Always returns a string. Invalid URLs (unparseable) and empty input
 * are passed through unchanged so the field still reflects exactly
 * what the tech pasted.
 */

type Strategy = 'dropQuery' | 'keepFirstParam';

const STRATEGY_BY_HOST: Record<string, Strategy> = {
  // Amazon — the path (`/dp/<asin>`) already identifies the product.
  'amazon.com.au': 'dropQuery',
  'amazon.com': 'dropQuery',
  // Broadway — uses `?product=<sku>` as the canonical identifier and
  // tacks `&search_content=…` (current search context) after it.
  'broadwaycomputers.net.au': 'keepFirstParam',
};

const normaliseHostname = (host: string): string =>
  host.toLowerCase().replace(/^www\./, '');

export const cleanSupplierUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return trimmed;
  }

  const strategy = STRATEGY_BY_HOST[normaliseHostname(parsed.hostname)];
  if (!strategy) return trimmed;

  if (strategy === 'dropQuery') {
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  }

  // keepFirstParam — drop every query param except the first; drop fragment too.
  const entries = Array.from(parsed.searchParams.entries());
  parsed.search = '';
  if (entries.length > 0) {
    const [k, v] = entries[0];
    parsed.searchParams.set(k, v);
  }
  parsed.hash = '';
  return parsed.toString();
};
